import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  MatchStatus,
  type AdminMatchDetail,
  type AdminMatchLiveState,
  type AdminMatchPlayerSummary,
} from '@shared/types';
import StatusPill from '../../components/StatusPill';
import Pill from '../../components/Pill';
import Card from '../../components/Card';
import Alert from '../../components/Alert';
import PageHeader from '../../components/PageHeader';
import PageSection from '../../components/PageSection';
import DataRow from '../../components/DataRow';
import { getMatch } from '../../services/matches';
import { ApiError } from '../../services/api';
import { formatDateTime } from '../../utils/date';
import { useGameStore } from '../../stores/gameStore';
import { adminModuleFor } from '../../games/registry';

/**
 * Admin match detail screen (read-only). Loads the full match on mount and renders:
 *
 *   - Header (short id, mode, status)
 *   - Two player cards (avatar placeholder, names linked to /users/:id, score)
 *   - Status section (started, ended, winner summary)
 *   - Live state section (only when `liveState !== null`) — current round,
 *     current turn, remaining seconds countdown, live scores, accepted players
 *   - The active game's own sections (its admin manifest's
 *     `matchDetailSections`, #817) — e.g. Word Clash's rounds + answers, which
 *     it fetches itself from `/admin/matches/:id/rounds`.
 *
 * Deliberately NO mutation affordance — admins observe matches, they do not
 * terminate them.
 */
export default function MatchDetail() {
  const { id } = useParams<{ id: string }>();
  const activeGameId = useGameStore((s) => s.activeGameId);
  const gameSections = adminModuleFor(activeGameId)?.matchDetailSections ?? [];

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; match: AdminMatchDetail }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  const fetchMatch = useCallback(async () => {
    if (!id) {
      setState({ status: 'error', message: 'Missing match id.' });
      return;
    }
    setState({ status: 'loading' });
    try {
      const data = await getMatch(id);
      setState({ status: 'ready', match: data });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load match';
      setState({ status: 'error', message });
    }
  }, [id]);

  useEffect(() => {
    void fetchMatch();
  }, [fetchMatch]);

  if (state.status === 'loading') {
    return (
      <div
        data-testid="match-detail-loading"
        className="text-sm text-slate-500"
      >
        Loading match…
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="space-y-4" data-testid="match-detail-error">
        <Link
          to="/matches"
          className="text-sm text-slate-600 hover:text-slate-900"
        >
          ← Back to Matches
        </Link>
        <Alert>{state.message}</Alert>
      </div>
    );
  }

  const match = state.match;

  return (
    <div className="space-y-6" data-testid="match-detail-screen">
      <PageHeader
        back={{
          to: '/matches',
          label: 'Back to Matches',
          testId: 'match-detail-back',
        }}
        title={
          <span
            className="font-mono"
            data-testid="match-detail-id"
            title={match.id}
          >
            {shortId(match.id)}
          </span>
        }
        badges={
          <>
            <Pill variant="neutral" testId="match-detail-mode">
              {match.mode}
            </Pill>
            <StatusPill status={match.status} />
          </>
        }
        subtitle={
          <>
            Language:{' '}
            <span className="uppercase">{match.language}</span>
            {' · '}
            {match.totalRounds} round{match.totalRounds === 1 ? '' : 's'}
          </>
        }
      />

      {/* Player cards ------------------------------------------------- */}
      {match.seats && match.seats.length > 2 ? (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="match-detail-seats">
        {match.seats.map((s) => (
          <PlayerCard
            key={s.seat}
            slot={String(s.seat + 1)}
            label={`Seat ${s.seat + 1}${s.team != null ? ` · Team ${s.team + 1}` : ''}`}
            player={s.player}
            score={s.score}
            isWinner={s.placement === 1}
            placement={s.placement}
            replacedByBot={s.replacedByBot}
          />
        ))}
      </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PlayerCard
          slot="1"
          player={match.player1}
          score={match.player1Score}
          isWinner={match.winnerId === match.player1.id}
        />
        <PlayerCard
          slot="2"
          player={match.player2}
          score={match.player2Score}
          isWinner={match.winnerId === match.player2.id}
        />
      </div>
      )}

      {/* Status section ----------------------------------------------- */}
      <PageSection
        title="Timing"
        testId="match-detail-status-section"
        bodySpacing="tight"
      >
        <DataRow label="Started" value={formatDateTime(match.startedAt)} />
        <DataRow
          label="Ended"
          value={match.endedAt ? formatDateTime(match.endedAt) : '—'}
          testId="match-detail-ended-at"
        />
        <DataRow
          label="Outcome"
          value={outcomeLabel(match)}
          testId="match-detail-outcome"
        />
      </PageSection>

      {/* Live state --------------------------------------------------- */}
      {match.liveState && (
        <LiveStateSection
          liveState={match.liveState}
          player1={match.player1}
          player2={match.player2}
        />
      )}

      {/* The active game's own sections (#817 — its admin manifest's
          `matchDetailSections`, e.g. Word Clash's rounds + answers). */}
      {gameSections.map((Section, i) => (
        <Section key={i} match={match} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PlayerCard({
  slot,
  label,
  player,
  score,
  isWinner,
  placement,
  replacedByBot,
}: {
  slot: string;
  /** Header text; absent = "Player {slot}" (the 1v1 card, unchanged). */
  label?: string;
  player: AdminMatchPlayerSummary;
  score: number;
  isWinner: boolean;
  placement?: number | null;
  replacedByBot?: boolean;
}) {
  const avatarLetter = (player.displayName || player.username || '?')
    .charAt(0)
    .toUpperCase();
  return (
    <Card
      padding="md"
      className="flex items-start gap-4"
      data-testid={`match-detail-player-${slot}`}
    >
      <div
        aria-hidden="true"
        className="w-14 h-14 rounded-full bg-slate-800 text-white flex items-center justify-center text-xl font-bold shrink-0"
        data-testid={`match-detail-player-${slot}-avatar`}
      >
        {avatarLetter}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-500 uppercase tracking-wide">
            {label ?? `Player ${slot}`}
          </span>
          {placement != null && (
            <Pill variant="neutral" testId={`match-detail-player-${slot}-placement`}>
              #{placement}
            </Pill>
          )}
          {replacedByBot && (
            <Pill variant="warning" testId={`match-detail-player-${slot}-replaced`}>
              Left · bot played out
            </Pill>
          )}
          {isWinner && (
            <Pill
              variant="success"
              testId={`match-detail-player-${slot}-winner`}
            >
              Winner
            </Pill>
          )}
        </div>
        <Link
          to={`/users/${player.id}`}
          data-testid={`match-detail-player-${slot}-link`}
          className="block text-base font-semibold text-slate-900 hover:text-slate-700 truncate"
        >
          {player.displayName}
        </Link>
        <div className="text-sm text-slate-600">@{player.username}</div>
        <div className="mt-2 flex items-center gap-2">
          <span
            data-testid={`match-detail-player-${slot}-score`}
            className="text-sm font-semibold tabular-nums text-slate-900"
          >
            Score: {score}
          </span>
        </div>
      </div>
    </Card>
  );
}

function LiveStateSection({
  liveState,
  player1,
  player2,
}: {
  liveState: AdminMatchLiveState;
  player1: AdminMatchPlayerSummary;
  player2: AdminMatchPlayerSummary;
}) {
  const remaining = useCountdownSeconds(liveState.turnDeadline);
  const currentTurnLabel =
    liveState.currentTurn === 'player1'
      ? `P1 (${player1.displayName})`
      : liveState.currentTurn === 'player2'
        ? `P2 (${player2.displayName})`
        : '—';

  const acceptedNames =
    liveState.acceptedPlayers.length > 0
      ? liveState.acceptedPlayers
          .map((uid) => {
            if (uid === player1.id) return `P1 (${player1.displayName})`;
            if (uid === player2.id) return `P2 (${player2.displayName})`;
            return uid;
          })
          .join(', ')
      : '—';

  return (
    <PageSection
      title="Live state"
      testId="match-detail-live-state"
      bodySpacing="tight"
    >
      <DataRow
        label="Current round"
        value={String(liveState.currentRound)}
        testId="live-current-round"
      />
      <DataRow
        label="Current turn"
        value={currentTurnLabel}
        testId="live-current-turn"
      />
      <DataRow
        label="Turn deadline"
        value={
          liveState.turnDeadline == null
            ? '—'
            : remaining == null
              ? '—'
              : `${remaining}s remaining`
        }
        testId="live-turn-deadline"
      />
      <DataRow
        label="Live score"
        value={`${liveState.player1Score} – ${liveState.player2Score}`}
        testId="live-score"
      />
      <DataRow
        label="Accepted players"
        value={acceptedNames}
        testId="live-accepted"
      />
    </PageSection>
  );
}

/**
 * Ticks once a second to recompute `(deadlineMs - Date.now()) / 1000`.
 * Returns `null` when the deadline is null or already past — callers
 * render "—" in that case.
 */
function useCountdownSeconds(deadlineMs: number | null): number | null {
  const compute = (): number | null => {
    if (deadlineMs == null) return null;
    const diff = Math.floor((deadlineMs - Date.now()) / 1000);
    if (diff <= 0) return null;
    return diff;
  };
  const [remaining, setRemaining] = useState<number | null>(compute);

  useEffect(() => {
    setRemaining(compute());
    if (deadlineMs == null) return;
    const handle = window.setInterval(() => {
      setRemaining(compute());
    }, 1000);
    return () => window.clearInterval(handle);
    // `compute` closes over deadlineMs; re-running the effect whenever the
    // deadline changes keeps the interval fresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadlineMs]);

  return remaining;
}

function outcomeLabel(match: AdminMatchDetail): string {
  if (match.status !== MatchStatus.Completed &&
      match.status !== MatchStatus.Abandoned) {
    return 'In progress';
  }
  if (match.seats && match.seats.length > 2) {
    const first = match.seats.filter((s) => s.placement === 1);
    if (first.length === 0) return 'No placements';
    if (first.length > 1) return `Shared 1st: seats ${first.map((s) => s.seat + 1).join(', ')}`;
    return `Seat ${first[0].seat + 1} wins`;
  }
  if (match.winnerId == null) return 'Draw';
  if (match.winnerId === match.player1.id) return 'P1 wins';
  if (match.winnerId === match.player2.id) return 'P2 wins';
  return match.winnerId;
}

function shortId(id: string): string {
  if (id.length <= 10) return id;
  return `${id.slice(0, 6)}…${id.slice(-4)}`;
}
